import React, { useState, useEffect } from "react";
import { Modal, Button, ButtonGroup, Table, Nav } from "react-bootstrap";
import styles from "./GenericFHIRViewerModal.module.css";
import PatientCardExtended from "./PatientCardExtended";
import ProviderCardExtended from "./ProviderCardExtended";
import EncounterCardExtended from "./EncounterCardExtended";
import OrganizationCardExtended from "./OrganizationCardExtended";
import CarePlanCardExtended from "./CarePlanCardExtended"; // <-- Add this import

// Helper to flatten FHIR objects for table display
function flattenObject(obj, parentKey = "") {
  let flat = {};
  for (let key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    const value = obj[key];

    if (value === null || value === undefined) {
      flat[newKey] = "";
    } else if (Array.isArray(value)) {
      if (value.every(v => typeof v === "object")) {
        value.forEach((item, index) => {
          const nested = flattenObject(item, `${newKey}[${index}]`);
          Object.assign(flat, nested);
        });
      } else {
        flat[newKey] = value.join(", ");
      }
    } else if (typeof value === "object") {
      Object.assign(flat, flattenObject(value, newKey));
    } else {
      flat[newKey] = String(value);
    }
  }
  return flat;
}

function isFhirReference(val) {
  return typeof val === "string" && /^[A-Za-z]+\/[A-Za-z0-9\-\.]+$/.test(val);
}

function isFhirResource(val) {
  return val && typeof val === "object" && val.resourceType && val.id;
}

const GenericFHIRViewerModal = ({ show, onClose, resource, title, fhirClient }) => {
  const resources = Array.isArray(resource) ? resource : resource ? [resource] : [];
  const [activeTab, setActiveTab] = useState(resources[0]?.id || "");
  const [viewMode, setViewMode] = useState("form"); // ✅ FORM is default
  const [nestedResource, setNestedResource] = useState(null);
  const [nestedTitle, setNestedTitle] = useState("");
  const [nestedShow, setNestedShow] = useState(false);
  const [nestedLoading, setNestedLoading] = useState(false);
  const [nestedError, setNestedError] = useState("");
  const [resolvedPatient, setResolvedPatient] = useState(null);
  const [resolvedProvider, setResolvedProvider] = useState(null);

  const currentResource = resources.find(r => r.id === activeTab) || resources[0];
  const flatData = currentResource && viewMode === "table" ? flattenObject(currentResource) : null;

  useEffect(() => {
    if (resources.length > 0) {
      setActiveTab(resources[0].id);
    }
  }, [resources]);

  useEffect(() => {
  const fetchReferences = async () => {
    if (!fhirClient || !currentResource || currentResource.resourceType !== "Encounter") return;

    const patientRef = currentResource.subject?.reference;
    const providerRef = currentResource.participant?.[0]?.individual?.reference;

    try {
      if (patientRef) {
        const patientRes = await fhirClient.request(patientRef);
        setResolvedPatient(patientRes);
      }

      if (providerRef) {
        const providerRes = await fhirClient.request(providerRef);
        setResolvedProvider(providerRes);
      } else {
        setResolvedProvider(null); // ✅ Clear stale provider data
      }
    } catch (err) {
      console.error("Failed to resolve references:", err);
      setResolvedProvider(null); // ✅ Defensive fallback
    }
  };

  fetchReferences();
}, [currentResource, fhirClient]);

  const getTabTitle = (r) => r.title ? `${r.title} - ${r.id}` : r.id;

  const handleShowNested = async (val, customTitle = "") => {
    setNestedError("");
    setNestedLoading(true);
    setNestedShow(true);

    if (isFhirResource(val)) {
      setNestedResource(val);

      let resolvedTitle = customTitle || `${val.resourceType}/${val.id}`;
      if (val.resourceType === "Organization" && typeof val.name === "string") {
        resolvedTitle = val.name;
      } else if (val.name?.[0]) {
        const nameObj = val.name[0];
        resolvedTitle = `${nameObj.prefix?.join(" ") || ""} ${nameObj.given?.join(" ")} ${nameObj.family || ""}`.trim();
      }

      setNestedTitle(resolvedTitle);
      setNestedLoading(false);
      return;
    }

    if (isFhirReference(val) && fhirClient) {
      try {
        const res = await fhirClient.request(val);
        setNestedResource(res);

        let resolvedTitle = customTitle || `${res.resourceType}/${res.id}`;
        if (res.resourceType === "Organization" && typeof res.name === "string") {
          resolvedTitle = res.name;
        } else if (res.name?.[0]) {
          const nameObj = res.name[0];
          resolvedTitle = `${nameObj.prefix?.join(" ") || ""} ${nameObj.given?.join(" ")} ${nameObj.family || ""}`.trim();
        }

        setNestedTitle(resolvedTitle);
      } catch (err) {
        setNestedError("Failed to fetch referenced resource.");
        setNestedResource(null);
        setNestedTitle(customTitle || "Unknown Resource");
      }
      setNestedLoading(false);
      return;
    }

    setNestedError("Not a FHIR resource or reference.");
    setNestedResource(null);
    setNestedTitle("Invalid Reference");
    setNestedLoading(false);
  };

  const renderValue = (value) => {
    if (isFhirResource(value)) {
      return (
        <Button variant="link" size="sm" style={{ padding: 0 }} onClick={() => handleShowNested(value)}>
          {value.resourceType}/{value.id}
        </Button>
      );
    }
    if (isFhirReference(value)) {
      return (
        <Button variant="link" size="sm" style={{ padding: 0 }} onClick={() => handleShowNested(value)}>
          {value}
        </Button>
      );
    }
    return String(value);
  };

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg" scrollable centered>
        <Modal.Header className="bg-light d-flex justify-content-between align-items-center">
          <Modal.Title className="mb-0">
            {title || (currentResource && currentResource.resourceType)}
          </Modal.Title>
          <ButtonGroup>
            <Button
              variant={viewMode === "form" ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => setViewMode("form")}
              disabled={!["Patient", "Practitioner", "Encounter", "CarePlan"].includes(currentResource?.resourceType)}
            >
              FORM
            </Button>
            <Button
              variant={viewMode === "table" ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => setViewMode("table")}
            >
              HTML
            </Button>
            <Button
              variant={viewMode === "json" ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => setViewMode("json")}
            >
              JSON
            </Button>
          </ButtonGroup>
        </Modal.Header>

        {resources.length > 1 && (
          <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="px-3 pt-2">
            {resources.map(r => (
              <Nav.Item key={r.id}>
                <Nav.Link eventKey={r.id}>{getTabTitle(r)}</Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}

        <Modal.Body className={styles.fhirViewerModal}>
          {viewMode === "json" && currentResource ? (
            <pre style={{ fontSize: "0.85rem", background: "#f8f9fa", padding: "1rem", borderRadius: "0.3rem" }}>
              {JSON.stringify(currentResource, null, 2)}
            </pre>
          ) : viewMode === "form" && currentResource?.resourceType === "Patient" ? (
            <PatientCardExtended resource={currentResource} />
          ) : viewMode === "form" && currentResource?.resourceType === "Practitioner" ? (
            <ProviderCardExtended resource={currentResource} />
          ) : viewMode === "form" && currentResource?.resourceType === "Encounter" ? (
            <EncounterCardExtended
              resource={currentResource}
              patientData={resolvedPatient}
              providerData={resolvedProvider}
              handleShowNested={handleShowNested}
            />
          ) : viewMode === "form" && currentResource?.resourceType === "Organization" ? (
            <OrganizationCardExtended resource={currentResource} />
          ) : viewMode === "form" && currentResource?.resourceType === "CarePlan" ? ( // <-- Add this block
            <CarePlanCardExtended resource={currentResource} />
          ) : flatData ? (
            <Table bordered responsive size="sm">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(flatData).map(([key, value]) => (
                  <tr key={key}>
                    <td><code>{key}</code></td>
                    <td>{renderValue(value)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>No resource data available.</p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {nestedShow && (
        <GenericFHIRViewerModal
          show={nestedShow}
          onClose={() => setNestedShow(false)}
          resource={nestedResource}
          title={nestedTitle}
          fhirClient={fhirClient}
        />
      )}
    </>
  );
};

export default GenericFHIRViewerModal;